import type { RecallKind } from '@prospero/shared';
import type { TFunction } from 'i18next';
import { diffDaysFromAppToday } from '../config/timezone';
import type { LotStatus } from '../services/lots/lots.types';

/**
 * `statusBadge` key (relative to the `lots` namespace) for a BLOCKED lot.
 * Falls back to the generic "ritiro/richiamo" wording when the blocking
 * case's kind is unknown (e.g. read via an endpoint that doesn't populate
 * `blockingRecallKind`, see `lot.repository.interface.ts`).
 */
export function getBlockedLotLabelSuffix(
  blockingRecallKind: RecallKind | null | undefined,
): string {
  if (blockingRecallKind === 'WITHDRAWAL') {
    return 'statusBadge.blockedWithdrawal';
  }
  if (blockingRecallKind === 'RECALL') {
    return 'statusBadge.blockedRecall';
  }
  return 'statusBadge.blocked';
}

export function getLotStatusColor(status: string): 'success' | 'error' | 'warning' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'EXPIRED':
      return 'error';
    case 'EXHAUSTED':
      return 'warning';
    case 'BLOCKED':
      return 'error';
    default:
      return 'success';
  }
}

export function getLotStatusLabel(
  status: string,
  t: TFunction,
  blockingRecallKind?: RecallKind | null,
): string {
  switch (status) {
    case 'ACTIVE':
      return t('lots:statusBadge.active');
    case 'EXPIRED':
      return t('lots:statusBadge.expired');
    case 'EXHAUSTED':
      return t('lots:statusBadge.exhausted');
    case 'BLOCKED':
      return t(`lots:${getBlockedLotLabelSuffix(blockingRecallKind)}`);
    default:
      return status;
  }
}

function isLotExpiredByDate(expiryDate: string | null | undefined): boolean {
  const diffDays = diffDaysFromAppToday(expiryDate);
  return diffDays !== null && diffDays < 0;
}

export function getEffectiveLotStatus(
  status: LotStatus | string,
  expiryDate: string | null | undefined,
): LotStatus {
  if (status === 'BLOCKED') {
    return 'BLOCKED';
  }
  if (status === 'EXHAUSTED') {
    return 'EXHAUSTED';
  }
  if (isLotExpiredByDate(expiryDate)) {
    return 'EXPIRED';
  }
  return status as LotStatus;
}
